import Styles from './setup.module.scss';
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext, ViewContext, Message } from 'components/lib';
import {
    Stepper,
    Step,
    StepLabel
} from '@mui/material';
import { ProfileInfoForm } from './profile';
import { PasswordSetupForm } from './password';
import { ContactVerificationForm } from './contact';

export default function ProfileSetup({ userDetails, t }) {
    const steps = ['Verify Contact', 'Set Password', 'Update Profile'];
    const viewContext = useContext(ViewContext);
    const authContext = useContext(AuthContext);
    const [localUserDetails, setLocalUserDetails] = useState(userDetails);
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({
        personalInfo: { email: userDetails?.email || '', phone: userDetails?.phone_number || '' },
        accountDetails: { password: '', confirmPassword: '' },
        profileInfo: { firstName: userDetails?.first_name || '', lastName: userDetails?.last_name || '', address: userDetails?.address || '', zipCode: userDetails?.zip_code || '' }
    });
    const [setupComplete, setSetupComplete] = useState(false);

    const { stepCompletionStatus, initialStep } = useMemo(() => {
        if (!localUserDetails) {
            return { stepCompletionStatus: [false, false, false, false], initialStep: 0 };
        }

        const stepCompletionStatus = [
            !!(localUserDetails.phone_number && localUserDetails.email),
            !!localUserDetails.has_password,
            !!(localUserDetails.first_name && localUserDetails.last_name && localUserDetails.zip_code && localUserDetails.onboarded),
        ];

        const firstIncompleteStep = stepCompletionStatus.findIndex((step) => !step);
        return {
            stepCompletionStatus,
            initialStep: firstIncompleteStep === -1 ? steps.length : firstIncompleteStep,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localUserDetails]);

    useEffect(() => {
        if (!localUserDetails) return;

        // If all steps are complete, set setupComplete to true
        if (initialStep === steps.length || localUserDetails.onboarded === 1) {
            setSetupComplete(true);
        } else {
            setActiveStep(initialStep);
        }
    }, [initialStep, localUserDetails, steps.length]);

    const getNextIncompleteStep = useCallback(
        (currentStep) => {
            for (let i = currentStep + 1; i < steps.length; i++) {
                if (!stepCompletionStatus[i]) {
                    return i;
                }
            }
            return steps.length;
        },
        [stepCompletionStatus, steps.length]
    );

    const handleStepCompletion = useCallback(
        (data, stepKey) => {
            setFormData((prev) => ({ ...prev, [stepKey]: data }));

            // Update local user details to reflect completed steps
            if (stepKey === 'personalInfo') {
                setLocalUserDetails(prev => ({
                    ...prev,
                    email: data.email || prev.email,
                    phone_number: data.phone || prev.phone_number
                }));
            } else if (stepKey === 'accountDetails') {
                setLocalUserDetails(prev => ({
                    ...prev,
                    has_password: true
                }));
            } else if (stepKey === 'profileInfo') {
                setLocalUserDetails(prev => ({
                    ...prev,
                    first_name: data.firstName || prev.first_name,
                    last_name: data.lastName || prev.last_name,
                    zip_code: data.zipCode || prev.zip_code,
                    address: data.address || prev.address,
                    onboarded: 1
                }));
            }

            const nextStep = getNextIncompleteStep(activeStep);
            if (nextStep === steps.length) {
                setSetupComplete(true);
                // Optional: you can leave this if you want to reload the page after completion
                // setTimeout(() => window.location.reload(), 2000);
            } else {
                setActiveStep(nextStep);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeStep, getNextIncompleteStep]
    );

    const renderStepContent = useCallback(
        (step) => {
            switch (step) {
                case 0:
                    return <ContactVerificationForm
                        onSuccess={(data) => handleStepCompletion(data, 'personalInfo')}
                        userData={localUserDetails}
                        context={viewContext}
                        authContext={authContext}
                        t={t}
                    />;
                case 1:
                    return <PasswordSetupForm
                        onSuccess={(data) => handleStepCompletion(data, 'accountDetails')}
                        initialData={formData.accountDetails}
                        context={viewContext}
                        authContext={authContext}
                    />;
                case 2:
                    return <ProfileInfoForm
                        onSuccess={(data) => handleStepCompletion(data, 'profileInfo')}
                        initialData={formData.profileInfo}
                        context={viewContext}
                        authContext={authContext}
                    />;
                default:
                    return null;
            }
        },
        [formData, handleStepCompletion, viewContext, authContext, localUserDetails, t]
    );

    return (
        <div id='ancestorContainer' className={Styles.ancestorContainer}>
            {setupComplete ? (
                <div id='messageContainer' className={Styles.messageContainer}>
                    <Message
                        type='success'
                        title={'Profile Setup Complete'}
                        text={'Your Profile has been  setup successfully'}
                    />
                </div>
            ) : (
                <div id='setUpContainer' className={Styles.setUpContainer}>
                    <Stepper
                        activeStep={activeStep}
                        alternativeLabel
                        className={Styles.stepper}
                        sx={{
                            '& .MuiStepLabel-label.Mui-active': {
                                fontWeight: 'bold',
                            }
                        }}
                    >
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    {renderStepContent(activeStep)}
                </div>
            )}
        </div>
    );
}